'use client';
import React, { useEffect, useState, useCallback, Suspense, useRef, useMemo } from 'react';
import ContentWrapper from '@/components/common/ContentWrapper';
import { ProductsPageContent } from '@/components/sections';
import { ProductsPageContentMobile } from '@/components/sections/mobile';
import { useResponsive } from '@/hooks/useResponsive';
import Loader from '@/components/ui/Loader';
import { ProductBase, Manufacturer, ProductFilters } from '@/types/product';
import { PaginationInfo } from '@/types/pagination';
import { BreadcrumbMenu, MenuRoute } from '@/types/menu';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { useProductsByCategory } from '@/hooks/useProducts';
import { useProductFilters } from '@/hooks/useProductFilters';
import { PaginationOptions } from '@/utils/serviceUtils';
import { useProductTracking } from '@/hooks/useProductTracking';

interface CategoryPageClientProps {
  breadcrumbMenu: BreadcrumbMenu;
  categoryIds: number[];
  products: ProductBase[];
  menuCategories: MenuRoute[];
  paginationInfo: PaginationInfo;
  manufacturers: Manufacturer[];
}

interface ProductsByCategoryProps {
  categoryIds: number[];
  lang: string;
  filters?: ProductFilters;
  sort?: string;
  pagination?: PaginationOptions;
}

const PAGE_LIMIT = 16;

const CategoryPageInner: React.FC<CategoryPageClientProps> = ({
  breadcrumbMenu,
  categoryIds,
  products,
  menuCategories,
  paginationInfo,
  manufacturers,
}) => {
  const { isMobile } = useResponsive();
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  const {
    selectedOptions,
    selectedManufacturer,
    priceRange,
    sortBy,
    setSortBy,
    setPriceRange,
    setSelectedManufacturer,
    setSelectedOptions,
  } = useProductFilters();

  const orderByParam = searchParams.get('sort');
  const optionsParam = searchParams.get('options');
  const pageParam = searchParams.get('page');

  const { trackProductListView } = useProductTracking();

  const [queryProps, setQueryProps] = useState<ProductsByCategoryProps>({
    categoryIds,
    lang: 'es',
    filters: {
      options: undefined,
      manufacturer: undefined,
      price_min: undefined,
      price_max: undefined,
    },
    sort: undefined,
    pagination: {
      page: undefined,
      limit: PAGE_LIMIT,
    },
  });

  // Función para actualizar la URL sin recargar la página
  const updateUrl = useCallback(
    (params: Record<string, string | undefined>) => {
      const query = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value && value.trim() !== '') query.set(key, value);
      });
      //const pathname = paginationInfo?.baseUrl || '';
      const newUrl = query.toString() ? `${pathname}?${query.toString()}` : pathname;
      router.replace(newUrl);
    },
    [router, pathname]
  );

  const { data: productsFiltered, isLoading } = useProductsByCategory(
    queryProps.categoryIds,
    queryProps.lang,
    queryProps.filters,
    queryProps.sort,
    queryProps.pagination,
    {
      enabled:
        !!queryProps.sort ||
        !!queryProps.filters?.price_min ||
        !!queryProps.filters?.price_max ||
        !!queryProps.filters?.manufacturer ||
        !!queryProps.filters?.options ||
        !!queryProps.pagination?.page,
    }
  );

  // Sincronizar el estado inicial con los parámetros de URL
  useEffect(() => {
    if (orderByParam || optionsParam || pageParam) {
      const options = optionsParam?.split(',').filter(Boolean) || [];
      const currentPage = Number(pageParam) || 1;
      const sort = orderByParam || 'position';

      setSortBy(sort);
      setSelectedOptions(options);

      setQueryProps((prev) => ({
        ...prev,
        sort: sort,
        filters: {
          ...prev.filters,
          options,
        },
        pagination: {
          ...prev.pagination,
          page: currentPage,
        },
      }));
    }
  }, [orderByParam, optionsParam, pageParam]);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (queryProps.sort && queryProps.sort !== 'position') {
      params.sort = queryProps.sort;
    }
    if (queryProps.filters?.options?.length) {
      params.options = queryProps.filters.options.join(',');
    }
    if (queryProps.pagination?.page && queryProps.pagination.page !== 1) {
      params.page = String(queryProps.pagination.page);
    }
    updateUrl(params);
  }, [queryProps, updateUrl]);

  const handleSortChange = useCallback(
    (sort: string) => {
      setSortBy(sort);
      setQueryProps((prev) => {
        const newState = {
          ...prev,
          sort,
          pagination: { ...prev.pagination, page: 1 }, // Reset a página 1
        };
        return newState;
      });
    },
    [setSortBy]
  );

  const handleOptionsChange = useCallback(
    (option: string) => {
      setQueryProps((prev) => {
        const currentOptions = prev.filters?.options || [];
        const newOptions = currentOptions.includes(option)
          ? currentOptions.filter((o) => o !== option)
          : [...currentOptions, option];

        setSelectedOptions(newOptions);

        const newState = {
          ...prev,
          filters: {
            ...prev.filters,
            options: newOptions,
          },
          pagination: { ...prev.pagination, page: 1 }, // Reset a página 1
        };
        return newState;
      });
    },
    [setSelectedOptions]
  );

  // Función para manejar múltiples opciones de una vez (para filtro móvil)
  const handleMultipleOptionsChange = useCallback(
    (newOptions: string[]) => {
      setSelectedOptions(newOptions);
      setQueryProps((prev) => {
        const newState = {
          ...prev,
          filters: {
            ...prev.filters,
            options: newOptions,
          },
          pagination: { ...prev.pagination, page: 1 }, // Reset a página 1
        };
        return newState;
      });
    },
    [setSelectedOptions]
  );

  const handleFilterChange = useCallback(
    (manufacturer?: number, price_min?: number, price_max?: number) => {
      setPriceRange([price_min || 0, price_max || 200]);
      setSelectedManufacturer(manufacturers.find((m) => m.id === manufacturer) || null);

      setQueryProps((prev) => {
        const newState = {
          ...prev,
          filters: {
            ...prev.filters,
            manufacturer,
            price_min,
            price_max,
          },
          pagination: { ...prev.pagination, page: 1 }, // Reset a página 1
        };
        return newState;
      });
    },
    [manufacturers, setPriceRange, setSelectedManufacturer]
  );

  const handlePageChange = useCallback((page: number) => {
    setQueryProps((prev) => {
      const newState = {
        ...prev,
        pagination: {
          ...prev.pagination,
          page,
        },
      };
      return newState;
    });
  }, []);

  const productFinal = productsFiltered?.data || products;
  const paginationInfoFinal = productsFiltered?.paginationInfo || paginationInfo;

  // Usar useRef para rastrear el último tracking y evitar duplicados
  const lastTrackingRef = useRef<string>('');

  // Memoizar los valores que se usan para el tracking
  const itemListId = useMemo(
    () =>
      breadcrumbMenu.menuLevel3?.slug ??
      breadcrumbMenu.menuLevel2?.slug ??
      breadcrumbMenu.menuLevel1.slug,
    [breadcrumbMenu]
  );

  const itemListName = useMemo(
    () =>
      breadcrumbMenu.menuLevel3?.name ??
      breadcrumbMenu.menuLevel2?.name ??
      breadcrumbMenu.menuLevel1.name,
    [breadcrumbMenu]
  );

  const currentPage = useMemo(
    () =>
      productsFiltered
        ? queryProps.pagination?.page || paginationInfoFinal.currentPage || 1
        : paginationInfo.currentPage || 1,
    [
      productsFiltered,
      queryProps.pagination?.page,
      paginationInfoFinal.currentPage,
      paginationInfo.currentPage,
    ]
  );

  useEffect(() => {
    const productsToTrack = productsFiltered?.data || products;

    if (!productsToTrack || productsToTrack.length === 0) {
      return;
    }

    // Crear una clave única para identificar este tracking
    const trackingKey = `${itemListId}-${currentPage}-${productsToTrack.length}-${productsToTrack[0]?.id}`;

    // Evitar tracking duplicado
    if (lastTrackingRef.current === trackingKey) {
      return;
    }

    const indexBase = (currentPage - 1) * PAGE_LIMIT;

    trackProductListView(
      productsToTrack.map((product, index) => ({
        index: indexBase + index,
        item_id: product.id.toString(),
        item_name: product.title,
        item_category: breadcrumbMenu.menuLevel1.name,
        item_category2: breadcrumbMenu.menuLevel2?.name,
        item_category3: breadcrumbMenu.menuLevel3?.name,
        item_list_id: itemListId,
        item_list_name: itemListName,
        item_brand: product.brand,
        price: product.priceFinalInc,
        discount: product.discountInc,
        bar_code: product.barcode,
      })),
      itemListId,
      itemListName
    );

    // Guardar la clave del último tracking
    lastTrackingRef.current = trackingKey;
  }, [
    productsFiltered,
    products,
    currentPage,
    itemListId,
    itemListName,
    breadcrumbMenu,
    trackProductListView,
  ]);

  return (
    <ContentWrapper data-component-id="src/app/(tienda)/[slug]/CategoryPageClient.tsx:330:4" data-component-path="src/app/(tienda)/[slug]/CategoryPageClient.tsx" data-component-line="330" data-component-file="CategoryPageClient.tsx" data-component-name="ContentWrapper" data-component-content="%7B%22elementName%22%3A%22ContentWrapper%22%7D">
      {isLoading ? (
        <div data-component-id="src/app/(tienda)/[slug]/CategoryPageClient.tsx:332:8" data-component-path="src/app/(tienda)/[slug]/CategoryPageClient.tsx" data-component-line="332" data-component-file="CategoryPageClient.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-center%20items-center%20py-8%22%7D" className="flex justify-center items-center py-8">
          <Loader data-component-id="src/app/(tienda)/[slug]/CategoryPageClient.tsx:333:10" data-component-path="src/app/(tienda)/[slug]/CategoryPageClient.tsx" data-component-line="333" data-component-file="CategoryPageClient.tsx" data-component-name="Loader" data-component-content="%7B%22elementName%22%3A%22Loader%22%7D" />
        </div>
      ) : isMobile ? (
        <ProductsPageContentMobile data-component-id="src/app/(tienda)/[slug]/CategoryPageClient.tsx:336:8" data-component-path="src/app/(tienda)/[slug]/CategoryPageClient.tsx" data-component-line="336" data-component-file="CategoryPageClient.tsx" data-component-name="ProductsPageContentMobile" data-component-content="%7B%22elementName%22%3A%22ProductsPageContentMobile%22%2C%22className%22%3A%22%22%7D"
          products={productFinal}
          menuCategories={menuCategories}
          breadcrumbMenu={breadcrumbMenu}
          className=""
          paginationInfo={paginationInfoFinal}
          manufacturers={manufacturers}
          sortBy={sortBy || 'position'}
          selectedOptions={selectedOptions}
          selectedManufacturer={selectedManufacturer}
          priceRange={priceRange || [0, 200]}
          categoryId={categoryIds?.[0]} // Usar el primer ID de categoría para secciones específicas
          handleSortChange={handleSortChange}
          handleOptionsChange={handleOptionsChange}
          handleMultipleOptionsChange={handleMultipleOptionsChange}
          handleFilterChange={handleFilterChange}
          handlePageChange={handlePageChange}
        />
      ) : (
        <ProductsPageContent data-component-id="src/app/(tienda)/[slug]/CategoryPageClient.tsx:355:8" data-component-path="src/app/(tienda)/[slug]/CategoryPageClient.tsx" data-component-line="355" data-component-file="CategoryPageClient.tsx" data-component-name="ProductsPageContent" data-component-content="%7B%22elementName%22%3A%22ProductsPageContent%22%2C%22className%22%3A%22%22%7D"
          breadcrumbMenu={breadcrumbMenu}
          products={productFinal}
          menuCategories={menuCategories}
          className=""
          paginationInfo={paginationInfoFinal}
          manufacturers={manufacturers}
          sortBy={sortBy || 'position'}
          selectedOptions={selectedOptions}
          selectedManufacturer={selectedManufacturer}
          priceRange={priceRange || [0, 200]}
          categoryId={categoryIds?.[0]} // Usar el primer ID de categoría para el componente de información
          handleSortChange={handleSortChange}
          handleOptionsChange={handleOptionsChange}
          handleFilterChange={handleFilterChange}
          handlePageChange={handlePageChange}
        />
      )}
    </ContentWrapper>
  );
};

const CategoryPageClient: React.FC<CategoryPageClientProps> = (props) => {
  return (
    <Suspense fallback={<Loader data-component-id="src/app/(tienda)/[slug]/CategoryPageClient.tsx:379:24" data-component-path="src/app/(tienda)/[slug]/CategoryPageClient.tsx" data-component-line="379" data-component-file="CategoryPageClient.tsx" data-component-name="Loader" data-component-content="%7B%22elementName%22%3A%22Loader%22%7D" />}>
      <CategoryPageInner data-component-id="src/app/(tienda)/[slug]/CategoryPageClient.tsx:380:6" data-component-path="src/app/(tienda)/[slug]/CategoryPageClient.tsx" data-component-line="380" data-component-file="CategoryPageClient.tsx" data-component-name="CategoryPageInner" data-component-content="%7B%22elementName%22%3A%22CategoryPageInner%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" {...props} />
    </Suspense>
  );
};

export default CategoryPageClient;
